import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from 'src/auth/AuthContext';
import { ROUTES } from 'src/routes';
import { themeColors } from 'src/themeColors';

export const MainMenu = () => {
  const navigate = useNavigate();
  const { user, profileDoc, logout, isAdmin } = useAuth();
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null,
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const doLogout = async () => {
    handleCloseUserMenu();
    try {
      navigate(ROUTES.HOME);
      await logout();
    } catch (err) {
      console.log(err);
    }
  };

  const pages = [
    {
      title: 'Dashboard',
      path: ROUTES.APP,
      show: !!user,
    },
    {
      title: 'Admin',
      path: ROUTES.ADMIN,
      show: isAdmin,
      children: [
        {
          title: 'Catalogs',
          path: ROUTES.ADMIN_CATALOGS,
        },
      ],
    },
    {
      title: 'Help',
      path: '/d/docs/intro/',
      show: !!user,
      children: [],
    },
  ];

  const getPageLinkProps = (page: { title: string; path: string }) => {
    if (page.title === 'Help') {
      return {
        component: 'a',
        href: page.path,
        target: '_blank',
        rel: 'noopener noreferrer',
      } as const;
    }

    return { component: NavLink, to: page.path } as const;
  };

  const settings = [
    {
      title: 'Account',
      action: () => {
        handleCloseUserMenu();
        navigate(ROUTES.ACCOUNT);
      },
      disabled: isAdmin,
      dataQaId: 'account-menu-button',
    },
    { title: 'Logout', action: doLogout, dataQaId: 'logout-menu-button' },
  ];

  return (
    <AppBar
      component="nav"
      variant="elevation"
      position="sticky"
      sx={{
        backgroundColor: themeColors.darkGray,
        position: 'sticky',
        top: 0,
      }}
    >
      <Container>
        <Toolbar disableGutters variant="dense">
          <Typography
            variant="h6"
            noWrap
            component={NavLink}
            to={ROUTES.HOME}
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              color: 'inherit',
              textDecoration: 'none',
              fontFamily: 'Roboto',
            }}
            data-qaid="main-menu-title"
          >
            Workbench Studio
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              data-qaid="main-menu-button"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map(
                (page) =>
                  page.show && (
                    <MenuItem
                      key={page.title}
                      onClick={handleCloseNavMenu}
                      {...getPageLinkProps(page)}
                    >
                      <Typography textAlign="center">{page.title}</Typography>
                    </MenuItem>
                  ),
              )}
            </Menu>
          </Box>
          <Typography
            variant="h5"
            noWrap
            // component={NavLink}
            // to={ROUTES.APP}
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            Workbench
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map(
              (page) =>
                page.show && (
                  <Button
                    key={page.title}
                    {...getPageLinkProps(page)}
                    variant="text"
                    onClick={handleCloseNavMenu}
                    sx={{ my: 2, color: 'white', display: 'block' }}
                    data-qaid={`main-menu-${page.title.toLowerCase()}-button`}
                  >
                    {page.title}
                  </Button>
                ),
            )}
          </Box>

          {!!user && (
            <Box sx={{ flexGrow: 0 }}>
              <Typography
                data-qaid="user-menu-button"
                onClick={handleOpenUserMenu}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                {(
                  profileDoc?.contractorName?.[0] || user?.email?.[0]
                )?.toUpperCase()}
              </Typography>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem disabled>{user.email}</MenuItem>
                {settings.map((setting) => (
                  <MenuItem
                    disabled={setting.disabled}
                    key={setting.title}
                    onClick={setting.action}
                    data-qaid={setting.dataQaId}
                  >
                    <Typography textAlign="center">{setting.title}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};
